// Pages has no shared WebDAV credential store. This browser owns its connections;
// the Worker receives credentials only in a same-origin POST body when needed.
export type BrowserDavInput = { name: string; url: string; rootPath?: string; username?: string; password?: string; token?: string };
type BrowserDavConnection = BrowserDavInput & { id: string; createdAt: string; updatedAt: string; revision: string };
const KEY = 'mineradio.webdav.connections.v1';
const fields = { name: 80, url: 4096, rootPath: 512, username: 160, password: 512, token: 512 };

function load(): BrowserDavConnection[] {
  let data;
  try { data = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { throw new Error('此浏览器的 WebDAV 连接记录无法读取，请检查浏览器存储。'); }
  if (!Array.isArray(data)) throw new Error('此浏览器的 WebDAV 连接记录无效。');
  return data;
}
function save(connections: BrowserDavConnection[]) {
  try { localStorage.setItem(KEY, JSON.stringify(connections)); }
  catch { throw new Error('浏览器无法保存 WebDAV 连接，请检查存储权限或可用空间。'); }
}
function validate(input: BrowserDavInput): BrowserDavInput {
  for (const [field, max] of Object.entries(fields)) {
    const value = input[field as keyof BrowserDavInput];
    if (value !== undefined && (typeof value !== 'string' || value.length > max || /[\u0000-\u001f\u007f]/.test(value))) throw new Error('连接字段格式或长度无效。');
  }
  if (!input.name?.trim()) throw new Error('请填写连接名称。');
  let url: URL;
  try { url = new URL(input.url); } catch { throw new Error('请填写有效的 HTTPS WebDAV 地址。'); }
  if (url.protocol !== 'https:' || url.port || url.username || url.password || url.search || url.hash) throw new Error('Pages WebDAV 需要无内嵌凭据的 HTTPS 地址（默认端口），不支持查询参数。');
  const host = url.hostname.toLowerCase();
  if (!host.includes('.') || /^[\d.]+$/.test(host) || host.includes(':') || /(?:^|\.)(?:localhost|local|internal|lan|home|test|invalid)\.?$/.test(host)) throw new Error('Pages WebDAV 仅支持公网域名，私网目录请使用 Node 部署。');
  return { ...input, name: input.name.trim(), url: url.href, rootPath: input.rootPath?.trim() || '/' };
}
export function publicBrowserDav(connection: BrowserDavConnection) {
  return { id: connection.id, name: connection.name, url: connection.url, rootPath: connection.rootPath || '/', credentialConfigured: Boolean(connection.password || connection.token), createdAt: connection.createdAt, updatedAt: connection.updatedAt };
}
export const browserDav = {
  list: () => load().map(publicBrowserDav),
  create(input: BrowserDavInput) {
    const fields = validate(input); const now = new Date().toISOString();
    const connection = { ...fields, id: crypto.randomUUID(), revision: crypto.randomUUID(), createdAt: now, updatedAt: now };
    save([...load(), connection]); return publicBrowserDav(connection);
  },
  update(id: string, input: Partial<BrowserDavInput>) {
    const connections = load(); const previous = connections.find(connection => connection.id === id);
    if (!previous) throw new Error('连接已删除，请重新添加。');
    const connection = { ...previous, ...validate({ ...previous, ...input }), updatedAt: new Date().toISOString(), revision: crypto.randomUUID() };
    save(connections.map(item => item.id === id ? connection : item)); return publicBrowserDav(connection);
  },
  remove(id: string) { save(load().filter(connection => connection.id !== id)); },
  revision(id: string) { return load().find(connection => connection.id === id)?.revision; },
  request(id: string, extra: Record<string, unknown> = {}, signal?: AbortSignal): RequestInit {
    const connection = load().find(connection => connection.id === id);
    if (!connection) throw new Error('此浏览器未保存该 WebDAV 连接，请在云盘中添加连接。');
    return { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...extra, connection: { id: connection.id, ...validate(connection) } }), signal };
  },
};

export function mergeBrowserDavScan<T extends { id: string; externalId: string; metadata?: Record<string, any>; createdAt?: string }>(previous: T[], result: { tracks?: T[]; completeDirectories?: string[]; recursive?: boolean; errors?: unknown[]; truncated?: boolean }) {
  const fresh = result.tracks || []; const freshIds = new Set(fresh.map(track => track.id));
  const visited = result.completeDirectories || [];
  const removeMissing = (track: T) => {
    if (result.recursive && !result.truncated && !result.errors?.length) return true;
    try {
      const path = new URL(track.externalId, 'https://dav.example.com').pathname;
      return visited.some(directory => path.startsWith(directory) && !path.slice(directory.length).includes('/'));
    } catch { return false; }
  };
  const retained = previous.filter(track => !freshIds.has(track.id) && !removeMissing(track));
  const tracks = [...retained, ...fresh.map(track => ({ ...previous.find(old => old.id === track.id), ...track, createdAt: previous.find(old => old.id === track.id)?.createdAt || new Date().toISOString() }))];
  const oldIds = new Set(previous.map(track => track.id));
  return { ...result, tracks, changes: { added: fresh.filter(track => !oldIds.has(track.id)).length, changed: fresh.filter(track => oldIds.has(track.id) && ['etag', 'lastModified'].some(key => previous.find(old => old.id === track.id)?.metadata?.[key] !== track.metadata?.[key])).length, removed: previous.filter(track => !tracks.some(current => current.id === track.id)).length, total: tracks.length } };
}
